// Dirección del negocio del registro: una línea para buscar y el desglose (calle,
// número, localidad, provincia y un adicional como "Local 1"), que es lo que se guarda.

export const CABA = 'Ciudad Autónoma de Buenos Aires';

export const PROVINCIAS = [
    'Buenos Aires', CABA, 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes',
    'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones',
    'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
    'Santiago del Estero', 'Tierra del Fuego', 'Tucumán',
];

// Cómo se comprobó la dirección, que se guarda con ella:
// - google: Google Maps tiene esa calle con esa altura en esa localidad.
// - cliente: Google no la encontró y el cliente confirmó que está bien escrita.
// - sin_google: no se pudo consultar a Google (sin key o caído).
// null mientras no se comprobó.
export const VERIFICADA_GOOGLE = 'google';
export const CONFIRMADA_CLIENTE = 'cliente';
export const SIN_GOOGLE = 'sin_google';

export const DIRECCION_VACIA = {
    calle: '', numero: '', adicional: '', localidad: '', partido: '', provincia: '',
    codigoPostal: '', lat: null, lng: null, placeId: null, verificacion: null,
};

// Sin tildes y en minúsculas. Con el texto en NFC conserva el largo, así los índices
// sirven para cortar el original.
const simple = (s) => String(s || '').normalize('NFC').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** "charlone" → "Charlone". Solo si vino todo en minúsculas o todo en mayúsculas. */
function capitalizar(s) {
    const t = String(s || '').trim();
    if (t !== t.toLowerCase() && t !== t.toUpperCase()) return t;
    return t.toLowerCase().replace(/(^|[\s.-])(\p{L})/gu, (m, sep, letra) => sep + letra.toUpperCase());
}

/** La provincia de la lista que nombra el texto ("Provincia de Buenos Aires" → "Buenos Aires"). */
export function provinciaDe(texto) {
    const t = simple(texto).trim();
    if (!t) return '';
    if (/ciudad autonoma|\bcaba\b|capital federal/.test(t)) return CABA;
    if (/\bbs\.?\s*as\b|^pba$|^gba$/.test(t)) return 'Buenos Aires';
    return PROVINCIAS.find((p) => p !== CABA && t.includes(simple(p))) || '';
}

/** La altura que se escribió, si no es parte del nombre de la calle ("Calle 12 1234" → "1234"). */
function alturaEscrita(escrito, calle) {
    const numeros = String(escrito || '').split(',')[0].match(/\b\d{1,5}\b/g) || [];
    const n = numeros[numeros.length - 1] || '';
    return n && !new RegExp(`\\b${n}\\b`).test(calle) ? n : '';
}

/** Desglose de una dirección elegida en Google Places (placesService.detalle). */
export function desdeGoogle(lugar, placeId, escrito) {
    const comp = (tipo) => (lugar.addressComponents || []).find((c) => (c.types || []).includes(tipo))?.longText || '';
    const provincia = provinciaDe(comp('administrative_area_level_1'));
    const barrio = comp('sublocality_level_1') || comp('sublocality');
    const calle = comp('route');
    return {
        calle,
        // Si se eligió una calle sin altura, el número sale de lo que se había escrito.
        numero: comp('street_number') || alturaEscrita(escrito, calle),
        adicional: comp('subpremise'),
        localidad: (provincia === CABA && barrio) || comp('locality') || barrio || comp('administrative_area_level_2'),
        partido: comp('administrative_area_level_2'),
        provincia,
        codigoPostal: comp('postal_code'),
        lat: lugar.location?.latitude ?? null,
        lng: lugar.location?.longitude ?? null,
        placeId: placeId || null,
        // Una calle sin altura (route) no confirma que el número exista.
        verificacion: comp('street_number') ? VERIFICADA_GOOGLE : null,
    };
}

const soloNumeros = (s) => String(s || '').replace(/\D/g, '');

/** Si el lugar de Google queda en la localidad escrita (o en su partido, o en el barrio de CABA). */
export function enLaLocalidad(lugar, d) {
    if (d.provincia && lugar.provincia !== d.provincia) return false;
    if (d.provincia === CABA) return true;
    const escrita = simple(d.localidad).trim();
    return [lugar.localidad, lugar.partido].some((l) => simple(l).trim() === escrita);
}

/**
 * Los lugares de buscarDireccion (placesService) que tienen la misma altura que la
 * dirección escrita, ya desglosados y sin repetidos.
 */
export function conLaAltura(lugares, d) {
    const vistos = new Set();
    return lugares
        .map((l) => desdeGoogle(l, l.id))
        .filter((l) => l.verificacion === VERIFICADA_GOOGLE && soloNumeros(l.numero) === soloNumeros(d.numero))
        .filter((l) => {
            const clave = simple(componer(l));
            if (vistos.has(clave)) return false;
            vistos.add(clave);
            return true;
        });
}

/** Link a Google Maps con la ubicación verificada. */
export function linkAlMapa(d) {
    if (d.lat == null || d.lng == null) return '';
    const params = new URLSearchParams({ api: '1', query: `${d.lat},${d.lng}` });
    if (d.placeId) params.set('query_place_id', d.placeId);
    return `https://www.google.com/maps/search/?${params}`;
}

/**
 * Desglose de una dirección escrita en una línea, sin Google:
 * "charlone 1095, san miguel buenos aires" → Charlone / 1095 / San Miguel / Buenos Aires.
 */
export function desdeTexto(linea) {
    const partes = String(linea || '').normalize('NFC').split(',').map((p) => p.trim()).filter(Boolean);
    const m = (partes[0] || '').match(/^(.*?\D)\s*\b(\d{1,5}|s\/n)\b\s*(.*)$/i);
    let [calle, numero, adicional] = m ? [m[1], m[2].toUpperCase(), m[3]] : [partes[0], '', ''];
    // "Calle 12 1234": el primer número es parte del nombre de la calle.
    const otro = adicional.match(/^(\d{1,5})\b\s*(.*)$/);
    if (otro) [calle, numero, adicional] = [`${calle.trim()} ${numero}`, otro[1], otro[2]];
    let localidad = '';
    let provincia = '';
    for (const parte of partes.slice(1)) {
        const p = provinciaDe(parte);
        if (p && !provincia) {
            provincia = p;
            // "san miguel buenos aires": lo que está antes de la provincia es la localidad.
            const t = simple(parte);
            const desde = t.includes(simple(p)) ? t.indexOf(simple(p)) : t.search(/\bbs\.?\s*as\b|\bcaba\b|\bcapital federal\b/);
            const antes = desde > 0 ? parte.slice(0, desde).replace(/[\s-]+$/, '') : '';
            if (!localidad && antes) localidad = antes;
        } else if (!localidad && !p) {
            localidad = parte;
        }
    }
    return {
        ...DIRECCION_VACIA,
        calle: capitalizar(calle),
        numero,
        adicional,
        localidad: capitalizar(localidad) || (provincia === CABA ? 'CABA' : ''),
        provincia,
    };
}

/** La dirección en una línea: "Charlone 1095, Local 1, San Miguel, Buenos Aires". */
export function componer(d) {
    const calleYNumero = [d.calle, d.numero].filter(Boolean).join(' ');
    const provincia = d.provincia === CABA ? 'CABA' : d.provincia;
    const localidad = d.localidad && simple(d.localidad) !== simple(provincia) ? d.localidad : '';
    return [calleYNumero, d.adicional, localidad, provincia].map((s) => (s || '').trim()).filter(Boolean).join(', ');
}

/** Lo que falta para que la dirección esté completa (vacío si está completa). */
export function faltantes(d) {
    return [
        !d.calle.trim() && 'calle',
        !d.numero.trim() && 'número',
        !d.localidad.trim() && 'localidad',
        !d.provincia && 'provincia',
    ].filter(Boolean);
}
